import { useTranslation } from "react-i18next"
import { config } from "@oysterhr/core"
import {
  Text,
  FormLabel,
  LoadingSpinner,
  TextInput,
  Button,
  Icon,
  RadioList,
  Radio,
} from "@oysterhr/ui"
import { CountryGroup, CountrySelect } from "@oysterhr/feature-components"
import { Controller } from "react-hook-form"
import {
  nonPrioritizedHireableCountries,
  prioritizedCountries,
  useEffectOnce,
} from "@oysterhr/common"
import SubdivisionSelect from "~/app/components/SubdivisionSelect"
import { useParams } from "react-router-dom"
import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import { PaymentMethodsCustomerBankAccountSepaType } from "@oysterhr/schema"
import { VerticalFormStack } from "~/shared/components/VerticalFormStack"
import { PaymentMethodEnumType } from "~/app/utils/constants"
import { useNewCompanyPaymentMethods } from "../../../../../hooks/useNewCompanyPaymentMethods"
import { useNewAddSepaPaymentMethodForm } from "../../hooks/useNewAddSepaPaymentMethodForm"
import { PaymentMethodCategoryEnumType } from "../../../../../enums"

const countryOptions: CountryGroup[] = [
  { label: "Priority countries", options: prioritizedCountries },
  {
    label: "Non-priority countries",
    options: nonPrioritizedHireableCountries,
  },
]

// This is a duplication of BankAccountDetails within sepa folder that will be modified during the Payment Mandate Epic
// Once the epic is complete and fully tested, this will be renamed to BankAccountDetails and the old component will be removed

export const NewBankAccountDetails = () => {
  const { paymentMethodType } = useParams()
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/SepaFlow", {
    keyPrefix: "bankDetails",
  })

  const [translateMandate] = useTranslation(
    "company_interface/CompanyPaymentMethodsPage/ConfirmMandateSignerModal",
  )
  const { accountAspect: companyId } = useAccountAspect()
  const { data } = useNewCompanyPaymentMethods({
    variables: { companyId },
  })

  const {
    onSubmit,
    handleSubmit,
    loading,
    register,
    control,
    watch,
    setValue,
    formState: { errors },
    formIsReady: isFormReady,
  } = useNewAddSepaPaymentMethodForm()

  const selectedCountry = watch("countryCode")
  const customerBankAccount = data?.company.customerBankAccount
  const paymentMethod = data?.company.paymentMethod
  const isFTEPaymentMethod = paymentMethodType === PaymentMethodCategoryEnumType.Fte

  if (config.nonProductionEnvironment) {
    console.log("SEPA IBAN: AT611904300234573201")
    console.log("SEPA BIC: BPKOPLPW")
  }

  const prefillDetails = () => {
    // Use FTE payment method to fill contractor form fields
    if (!customerBankAccount) return

    const { accountHolderName, accountNumber, email, billingAddress, swiftBicCode } =
      customerBankAccount as PaymentMethodsCustomerBankAccountSepaType

    setValue("name", accountHolderName)
    setValue("iban", accountNumber)
    setValue("email", email)
    setValue("addressLine1", billingAddress.addressLine1 ?? "")
    setValue("addressLine2", billingAddress.addressLine2 ?? "")
    setValue("postalCode", billingAddress.postalCode ?? "")
    setValue("countryCode", billingAddress.countryCode ?? "")
    setValue("region", billingAddress.region ?? "")
    setValue("city", billingAddress.city ?? "")

    if (!isFTEPaymentMethod) return
    setValue("isUserSigner", !customerBankAccount.userNotSigner)
    setValue("swiftBicCode", swiftBicCode)
  }

  useEffectOnce(() => {
    const shouldPrefillForContractor =
      !isFTEPaymentMethod && paymentMethod?.__typename === PaymentMethodEnumType.STRIPE_SEPA

    const shouldPrefillForFTE =
      isFTEPaymentMethod &&
      customerBankAccount?.__typename === PaymentMethodEnumType.CUSTOMER_BANK_ACCOUNT_SEPA

    if (shouldPrefillForFTE || shouldPrefillForContractor) {
      prefillDetails()
    }
  })

  return (
    <div className="mt-8 text-left">
      <Text>{t("title")}</Text>
      {customerBankAccount && !isFTEPaymentMethod && (
        <div className="my-4 flex space-x-2 rounded-lg bg-info-lighten-100 p-4">
          {/* Replace with magic wand */}
          <div>
            <Icon name="MagicWand" className="text-info-base " size="md" />
          </div>
          <div className="flex flex-col space-y-2">
            <Text>{t("prefillDescription")}</Text>
            <Button variation="outline" size="sm" onClick={prefillDetails}>
              {t("btnPrefill")}
            </Button>
          </div>
        </div>
      )}

      <div className="max-w-md">
        {!isFormReady && (
          <div className="flex justify-center" role="progressbar">
            <LoadingSpinner />
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <VerticalFormStack>
            <TextInput
              {...register("name")}
              label={<FormLabel label={t("accountHolderName")} />}
              error={errors?.name?.message}
              disabled={!isFormReady}
            />

            {!isFTEPaymentMethod && (
              <TextInput
                {...register("email")}
                label={<FormLabel label={t("email")} />}
                error={errors?.email?.message}
                disabled={!isFormReady}
                type="email"
              />
            )}

            <TextInput
              {...register("iban")}
              label={<FormLabel label={t("iban")} />}
              error={errors?.iban?.message}
              disabled={!isFormReady}
            />

            {isFTEPaymentMethod && (
              <TextInput
                {...register("swiftBicCode")}
                label={<FormLabel label={t("swiftBicCode")} />}
                error={errors?.swiftBicCode?.message}
                disabled={!isFormReady}
              />
            )}

            <TextInput
              {...register("addressLine1")}
              label={<FormLabel label={t("addressLine1")} />}
              error={errors?.addressLine1?.message}
              disabled={!isFormReady}
            />

            <TextInput
              {...register("addressLine2")}
              label={<FormLabel label={t("addressLine2")} />}
              error={errors?.addressLine2?.message}
              disabled={!isFormReady}
            />

            <TextInput
              {...register("postalCode")}
              label={<FormLabel label={t("postalCode")} />}
              error={errors?.postalCode?.message}
              disabled={!isFormReady}
            />

            <Controller
              name="countryCode"
              control={control}
              render={({ field: { name, value, onChange } }) => (
                <CountrySelect
                  name={name}
                  error={errors.countryCode?.message}
                  label={<FormLabel label={t("countryCode")} />}
                  placeholder={t("countryCodePlaceholder")}
                  countries={countryOptions}
                  value={value}
                  onChange={option => onChange(option ?? undefined)}
                />
              )}
            />

            <Controller
              name="region"
              control={control}
              render={({ field: { value, onChange, name } }) => (
                <SubdivisionSelect
                  selectedCountry={selectedCountry}
                  label={<FormLabel label={t("region")} />}
                  name={name}
                  placeholder={t("regionPlaceholder")}
                  error={errors.region?.message}
                  value={value}
                  onChange={onChange}
                  disableUnavailableSubdivisions
                />
              )}
            />

            <TextInput
              {...register("city")}
              label={<FormLabel label={t("city")} />}
              error={errors?.city?.message}
              disabled={!isFormReady}
            />

            {isFTEPaymentMethod && (
              <>
                <Controller
                  name="isUserSigner"
                  control={control}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <RadioList
                      onChange={event => onChange(event.target.value === "true")}
                      label={<FormLabel label={translateMandate("isUserSigner.label")} />}
                      error={error?.message}
                      orientation="horizontal"
                    >
                      <Radio
                        checked={value}
                        value="true"
                        label={<FormLabel label={translateMandate("yes")} />}
                      />
                      <Radio
                        checked={!value}
                        value="false"
                        label={<FormLabel label={translateMandate("no")} />}
                      />
                    </RadioList>
                  )}
                />

                <TextInput
                  {...register("email")}
                  type="email"
                  label={
                    <FormLabel
                      label={translateMandate("email.label")}
                      assistiveText={
                        watch("isUserSigner")
                          ? translateMandate("email.userIsSigner")
                          : translateMandate("email.userNotSigner")
                      }
                    />
                  }
                  error={errors.email?.message}
                />
              </>
            )}
          </VerticalFormStack>

          <div className="flex justify-start">
            <Button
              data-analytics-name="company_payment_methods-sepa-create-payment-method"
              className="mt-8"
              disabled={!isFormReady || loading}
              isLoading={loading}
            >
              {t("btnSubmit")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
